import { useState } from 'react';
import { ROLES } from '@shared/types';
import type { Role, User } from '@shared/types';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useAsync } from '../lib/useAsync';
import { useToast } from '../lib/toast';
import { dateLabel } from '../lib/format';
import { Banner, Field, LoadingPage, Tag } from '../components/ui';
import { PageHeader } from '../components/PageHeader';

const MANAGE_ROLES: Role[] = ['admin', 'board', 'arb', 'organizer'];

type ResidentType = 'owner' | 'tenant';

function ResidentTypeToggle({
  value,
  onChange,
  disabled,
  id,
}: {
  value: ResidentType;
  onChange: (next: ResidentType) => void;
  disabled?: boolean;
  id?: string;
}) {
  return (
    <div
      className="segment-toggle"
      role="radiogroup"
      aria-label="Resident type"
      id={id}
    >
      <button
        type="button"
        role="radio"
        aria-checked={value === 'owner'}
        className={value === 'owner' ? 'on' : ''}
        disabled={disabled}
        onClick={() => onChange('owner')}
      >
        Owner
      </button>
      <button
        type="button"
        role="radio"
        aria-checked={value === 'tenant'}
        className={value === 'tenant' ? 'on' : ''}
        disabled={disabled}
        onClick={() => onChange('tenant')}
      >
        Tenant
      </button>
    </div>
  );
}

export function ResidentsPage() {
  const { user } = useAuth();
  const { notify, fail } = useToast();
  const [query, setQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [residentFilter, setResidentFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [busy, setBusy] = useState(false);
  const [importBusy, setImportBusy] = useState(false);
  const [preview, setPreview] = useState<{
    jobId: string;
    valid: number;
    invalid: number;
    errors: { row: number; errors: string[] }[];
  } | null>(null);

  const [invite, setInvite] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    unit: '',
    note: '',
    isTenant: false,
    roles: ['member'] as Role[],
  });

  const residents = useAsync(
    async () =>
      (
        await api.residents({
          q: query || undefined,
          role: roleFilter || undefined,
          resident: residentFilter || undefined,
          status: statusFilter || undefined,
        })
      ).users,
    [query, roleFilter, residentFilter, statusFilter],
  );
  const invites = useAsync(async () => (await api.invites()).invites, []);
  const imports = useAsync(async () => (await api.importHistory()).jobs, []);

  async function toggleRole(person: User, role: Role) {
    if (!window.confirm(`Update ${person.name}'s ${ROLES[role]} access?`)) return;
    const next = person.roles.includes(role)
      ? person.roles.filter((r) => r !== role)
      : [...person.roles, role];
    if (!next.includes('member')) next.unshift('member');
    try {
      const { user: updated } = await api.setRoles(person.id, next, {
        isTenant: person.isTenant,
        membershipStatus: person.membershipStatus,
      });
      residents.setData((residents.data ?? []).map((p) => (p.id === updated.id ? updated : p)));
      notify(`${updated.name} is now ${updated.roles.map((r) => ROLES[r]).join(', ')}.`, 'success');
    } catch (error) {
      fail(error, 'Those roles were not saved.');
    }
  }

  async function setResidentType(person: User, next: ResidentType) {
    const isTenant = next === 'tenant';
    if (person.isTenant === isTenant) return;
    try {
      const { user: updated } = await api.setRoles(person.id, person.roles, { isTenant });
      residents.setData((residents.data ?? []).map((p) => (p.id === updated.id ? updated : p)));
      notify(
        updated.isTenant
          ? `${updated.name} is a tenant (events and property violations only).`
          : `${updated.name} is an owner (architecture review, events, and property violations).`,
        'success',
      );
    } catch (error) {
      fail(error, 'The resident type was not saved.');
    }
  }

  async function toggleSuspension(person: User) {
    const suspending = person.membershipStatus !== 'suspended';
    if (
      !window.confirm(
        suspending
          ? `Suspend ${person.name}'s membership? They will not be able to sign in.`
          : `Reactivate ${person.name}'s membership?`,
      )
    ) {
      return;
    }
    try {
      const { user: updated } = await api.setRoles(person.id, person.roles, {
        membershipStatus: suspending ? 'suspended' : 'active',
        isTenant: person.isTenant,
      });
      residents.setData((residents.data ?? []).map((p) => (p.id === updated.id ? updated : p)));
      notify(
        updated.membershipStatus === 'suspended'
          ? `${updated.name} is suspended.`
          : `${updated.name} is active again.`,
        'success',
      );
    } catch (error) {
      fail(error, 'Membership status was not updated.');
    }
  }

  async function createInvite() {
    setBusy(true);
    try {
      const { code, emailed, mailError } = await api.createInvite({
        roles: invite.roles,
        note: invite.note.trim() || undefined,
        email: invite.email.trim(),
        firstName: invite.firstName.trim(),
        lastName: invite.lastName.trim(),
        phone: invite.phone.trim(),
        unit: invite.unit.trim(),
        maxUses: 1,
        expiresInDays: 7,
        isTenant: invite.isTenant,
      });
      await invites.reload();
      setInvite({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        unit: '',
        note: '',
        isTenant: false,
        roles: ['member'],
      });
      notify(
        emailed
          ? `Invitation sent to ${invite.email} (code ${code}).`
          : `Invitation ${code} created, but the email was not sent.${mailError ? ` ${mailError}` : ''}`,
        emailed ? 'success' : 'error',
      );
    } catch (error) {
      fail(error, 'The invitation was not created.');
    } finally {
      setBusy(false);
    }
  }

  async function onImportFile(file: File | null) {
    if (!file) return;
    setImportBusy(true);
    try {
      const { job, rows } = await api.importPreview(file);
      setPreview({
        jobId: job.id,
        valid: job.validRows,
        invalid: job.invalidRows,
        errors: rows.filter((r) => !r.valid).map((r) => ({ row: r.rowNumber, errors: r.errors })),
      });
      await imports.reload();
      notify(
        `Preview ready: ${job.validRows} valid, ${job.invalidRows} invalid of ${job.totalRows}.`,
        'info',
      );
    } catch (error) {
      fail(error, 'The import file could not be read.');
    } finally {
      setImportBusy(false);
    }
  }

  async function commitImport() {
    if (!preview) return;
    if (
      !window.confirm(
        `Import ${preview.valid} valid row(s) and send invitations? Invalid rows will be skipped.`,
      )
    ) {
      return;
    }
    setImportBusy(true);
    try {
      const result = await api.importCommit(preview.jobId, true);
      setPreview(null);
      await Promise.all([invites.reload(), imports.reload()]);
      notify(`Imported ${result.imported} members; sent ${result.invited} invitations.`, 'success');
    } catch (error) {
      fail(error, 'The import was not committed.');
    } finally {
      setImportBusy(false);
    }
  }

  if (residents.loading && !residents.data) return <LoadingPage label="Loading members…" />;
  const list = residents.data ?? [];

  return (
    <div>
      <PageHeader
        title="Member Management"
        lede="Invite members, assign Admin or Board Member roles, suspend memberships, and import a roster. Each resident is either an Owner or a Tenant — never both. Owners can submit architectural review requests, view events, and see property violations; tenants can view events and property violations only."
      />

      <div className="card">
        <h3>Invite a member</h3>
        <div className="row" style={{ gap: 12 }}>
          <Field label="First name">
            <input
              value={invite.firstName}
              onChange={(e) => setInvite({ ...invite, firstName: e.target.value })}
              required
            />
          </Field>
          <Field label="Last name">
            <input
              value={invite.lastName}
              onChange={(e) => setInvite({ ...invite, lastName: e.target.value })}
              required
            />
          </Field>
        </div>
        <Field label="Email">
          <input
            type="email"
            value={invite.email}
            onChange={(e) => setInvite({ ...invite, email: e.target.value })}
            required
          />
        </Field>
        <Field label="Phone">
          <input
            type="tel"
            value={invite.phone}
            onChange={(e) => setInvite({ ...invite, phone: e.target.value })}
            required
          />
        </Field>
        <Field label="Property address">
          <input
            value={invite.unit}
            onChange={(e) => setInvite({ ...invite, unit: e.target.value })}
            required
            placeholder="18 Willow Drive"
          />
        </Field>
        <div className="row" style={{ marginTop: 10 }}>
          {MANAGE_ROLES.map((role) => (
            <label key={role} className="check" style={{ marginRight: 8 }}>
              <input
                type="checkbox"
                checked={invite.roles.includes(role)}
                onChange={(event) =>
                  setInvite({
                    ...invite,
                    roles: event.target.checked
                      ? [...invite.roles, role]
                      : invite.roles.filter((r) => r !== role),
                  })
                }
              />
              <span>{ROLES[role]}</span>
            </label>
          ))}
        </div>
        <Field
          label="Resident type"
          hint={
            invite.isTenant
              ? 'Tenant: events and property violations only. Uses the Tenant invitation email template.'
              : 'Owner: architectural review, events, and property violations. Uses the Owner invitation email template.'
          }
        >
          <ResidentTypeToggle
            value={invite.isTenant ? 'tenant' : 'owner'}
            onChange={(next) => setInvite({ ...invite, isTenant: next === 'tenant' })}
          />
        </Field>
        <Field label="Internal note">
          <input
            value={invite.note}
            onChange={(e) => setInvite({ ...invite, note: e.target.value })}
            placeholder="Optional note for board records"
          />
        </Field>
        <button
          type="button"
          className="btn"
          disabled={
            busy ||
            !invite.email ||
            !invite.firstName ||
            !invite.lastName ||
            !invite.phone ||
            !invite.unit
          }
          onClick={() => void createInvite()}
        >
          {busy ? 'Sending…' : 'Send invitation'}
        </button>
      </div>

      <div className="card">
        <h3>Bulk import (CSV)</h3>
        <p className="mut">
          Headers: first_name, last_name, property_address, resident_type, email, phone,
          community_role. Set <code>resident_type</code> to <code>owner</code> or{' '}
          <code>tenant</code> (mutually exclusive; <code>homeowner</code> is accepted as an alias
          for owner). After you confirm, each row gets the matching Owner or Tenant invitation
          email. Consent is collected when each invitee accepts — not from the file.
        </p>
        <div className="row" style={{ gap: 10, marginBottom: 10 }}>
          <a className="btn s" href="/api/imports/sample.csv">
            Download sample CSV
          </a>
          <label className="btn s">
            {importBusy ? 'Reading…' : 'Upload CSV'}
            <input
              type="file"
              accept=".csv,.xlsx,.xls,text/csv"
              hidden
              onChange={(e) => void onImportFile(e.target.files?.[0] ?? null)}
            />
          </label>
        </div>
        {preview && (
          <Banner kind={preview.invalid ? 'warn' : 'ok'}>
            Preview: {preview.valid} valid, {preview.invalid} invalid.
            {preview.errors.length > 0 && (
              <ul style={{ marginTop: 8 }}>
                {preview.errors.slice(0, 8).map((err) => (
                  <li key={err.row}>
                    Row {err.row}: {err.errors.join('; ')}
                  </li>
                ))}
              </ul>
            )}
            {preview.valid > 0 && (
              <div className="btn-row" style={{ marginTop: 10 }}>
                <button
                  type="button"
                  className="btn"
                  disabled={importBusy}
                  onClick={() => void commitImport()}
                >
                  Import valid rows &amp; send invitations
                </button>
              </div>
            )}
          </Banner>
        )}
        {(imports.data ?? []).length > 0 && (
          <div className="tw" style={{ marginTop: 12 }}>
            <table>
              <thead>
                <tr>
                  <th>File</th>
                  <th>Status</th>
                  <th>Valid</th>
                  <th>Imported</th>
                  <th>When</th>
                </tr>
              </thead>
              <tbody>
                {(imports.data ?? []).slice(0, 8).map((job) => (
                  <tr key={job.id}>
                    <td>{job.filename}</td>
                    <td>{job.status}</td>
                    <td>
                      {job.validRows}/{job.totalRows}
                    </td>
                    <td>{job.importedRows}</td>
                    <td className="mut">{dateLabel(job.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {(invites.data ?? []).length > 0 && (
        <div className="card">
          <h3>Invitations</h3>
          <div className="tw">
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Roles</th>
                  <th>Status</th>
                  <th>Expires</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {(invites.data ?? []).map((item) => (
                  <tr key={item.code}>
                    <td>
                      {[item.firstName, item.lastName].filter(Boolean).join(' ') || '—'}
                    </td>
                    <td>{item.email ?? '—'}</td>
                    <td>{item.roles.map((role) => ROLES[role] ?? role).join(', ')}</td>
                    <td>
                      <Tag tone={item.status === 'sent' ? 'info' : 'neutral'}>{item.status}</Tag>
                    </td>
                    <td className="mut">
                      {item.expiresAt ? dateLabel(item.expiresAt) : 'never'}
                    </td>
                    <td>
                      <div className="row" style={{ flexWrap: 'wrap', gap: 6 }}>
                        {['pending', 'sent', 'delivery_failed'].includes(item.status) && (
                          <>
                            <button
                              type="button"
                              className="btn s tiny"
                              title="Send the same invitation code again"
                              onClick={() =>
                                void api
                                  .resendInvite(item.code)
                                  .then(({ emailed, mailError }) => {
                                    void invites.reload();
                                    notify(
                                      emailed
                                        ? 'Invitation resent with the same code.'
                                        : `Invitation updated, but the email was not sent.${mailError ? ` ${mailError}` : ''}`,
                                      emailed ? 'success' : 'error',
                                    );
                                  })
                                  .catch((error) => fail(error, 'Could not resend.'))
                              }
                            >
                              Resend
                            </button>
                            <button
                              type="button"
                              className="btn s tiny"
                              onClick={() =>
                                void api
                                  .revokeInvite(item.code)
                                  .then(() => invites.reload())
                                  .then(() => notify('Invitation revoked.', 'success'))
                                  .catch((error) => fail(error, 'Could not revoke.'))
                              }
                            >
                              Revoke
                            </button>
                          </>
                        )}
                        {['pending', 'revoked'].includes(item.status) && (
                          <button
                            type="button"
                            className="btn s tiny"
                            onClick={() => {
                              if (
                                !window.confirm(
                                  `Delete this ${item.status} invitation? This cannot be undone.`,
                                )
                              ) {
                                return;
                              }
                              void api
                                .deleteInvite(item.code)
                                .then(() => invites.reload())
                                .then(() => notify('Invitation deleted.', 'success'))
                                .catch((error) => fail(error, 'Could not delete.'));
                            }}
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="card">
        <div className="card-head">
          <h3>Members</h3>
          <span className="mut">{list.length} shown</span>
        </div>
        <div className="row" style={{ gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search name, email, phone, address"
            style={{ minWidth: 220, flex: 1 }}
          />
          <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
            <option value="">All roles</option>
            <option value="member">Member</option>
            <option value="admin">Admin</option>
            <option value="board">Board Member</option>
          </select>
          <select value={residentFilter} onChange={(e) => setResidentFilter(e.target.value)}>
            <option value="">Owner &amp; tenant</option>
            <option value="homeowner">Owner</option>
            <option value="tenant">Tenant</option>
          </select>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">All statuses</option>
            <option value="active">Active</option>
            <option value="suspended">Suspended</option>
          </select>
        </div>

        {list.length === 0 && <Banner>No members match those filters.</Banner>}

        <div className="stack" style={{ marginTop: 14 }}>
          {list.map((person) => (
            <div key={person.id} style={{ borderTop: '1px solid var(--bd)', paddingTop: 12 }}>
              <div className="card-head">
                <div>
                  <h4>
                    {person.name}
                    {person.id === user?.id && <span className="mut"> (you)</span>}
                  </h4>
                  <div className="mut">
                    {person.email}
                    {person.unit ? ` · ${person.unit}` : ''}
                    {person.phone ? ` · ${person.phone}` : ''} · joined {dateLabel(person.createdAt)}
                  </div>
                </div>
                <div className="tag-row">
                  {person.membershipStatus === 'suspended' && <Tag tone="bad">Suspended</Tag>}
                  {person.isTenant ? (
                    <Tag tone="warn">Tenant</Tag>
                  ) : (
                    <Tag tone="ok">Owner</Tag>
                  )}
                  {person.roles.map((role) => (
                    <Tag key={role} tone={role === 'member' ? 'neutral' : 'info'}>
                      {ROLES[role] ?? role}
                    </Tag>
                  ))}
                </div>
              </div>

              <div className="row" style={{ marginTop: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                <ResidentTypeToggle
                  value={person.isTenant ? 'tenant' : 'owner'}
                  onChange={(next) => void setResidentType(person, next)}
                />
                {MANAGE_ROLES.map((role) => (
                  <button
                    key={role}
                    type="button"
                    className={person.roles.includes(role) ? 'btn tiny' : 'btn s tiny'}
                    onClick={() => void toggleRole(person, role)}
                  >
                    {person.roles.includes(role) ? `✓ ${ROLES[role]}` : `Grant ${ROLES[role]}`}
                  </button>
                ))}
                <button
                  type="button"
                  className="btn s tiny"
                  onClick={() => void toggleSuspension(person)}
                >
                  {person.membershipStatus === 'suspended' ? 'Reactivate' : 'Suspend'}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
